import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import {digitalSignageFlow, selectionTypeSubDisplay} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {useDispatch} from 'react-redux';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {InputField} from '@Components/input-field-v2';
import {getAssetUrl} from '@Utils/s3.util';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {Checkbox, CHECKBOX_TYPE} from '@Components/checkbox';
import {SubFlowType} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel.types';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {DisplayType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import type {DigitalSignageSubPanelProps} from './digital-signage-sub-panel.types';
import styles from './digital-signage-sub-panel.module.scss';
import {useAppSelector} from '@/hooks';
import type {RootState} from '@/store';
import '../../../../../../../../../assets/css/components/_animations.scss';
import {isMobileWidth} from '@Utils/browser.util';

export function DigitalSignageSubPanel({...props}: DigitalSignageSubPanelProps): ReactElement {
  const dispatch = useDispatch();
  const modalDetails = useAppSelector((state: RootState) => {
    return state.publishingOptionsPanel.displayType;
  });

  const {
    inputBarIcon,
    showCheckBox,
    checkBoxText,
    illustration,
    showInputBar,
    imgSrc,
    showButton,
    buttonText,
    buttonIcon,
    panelHeading,
    heading,
    subheading,
    webpageLink,
    listItem,
    onClick,
    isLoading,
    buttonComponent,
    headerClassName,
    contentClassName,
    loadingScreenText,
    onClose,
  } = props;

  const handleBackButton = (): void => {
    dispatch(digitalSignageFlow());
    dispatch(selectionTypeSubDisplay(SubFlowType.DEFAULT));
  };

  const [checked, setChecked] = useState(false);
  const getHeaderDropDown = (): ReactElement => {
    return (
      <PanelHeader
        title={panelHeading}
        showBackButton
        titleClasses="body-xs-bold"
        backButtonIconSize={IconSize.SIZE_ICON_16}
        onBackButton={handleBackButton}
        showCloseButton={false}
        className={`_no-border-bottom spacing-p-0 ${styles.settingPanelHeightToAuto} ${headerClassName}`}
      />
    );
  };

  const getHeaderPanel = (): ReactElement => {
    return <PanelHeader title={panelHeading} showBackButton titleClasses="body-s-bold" onBackButton={handleBackButton} showCloseButton onClose={onClose} />;
  };

  const getContent = (): ReactElement => {
    return (
      <div
        className={` ${contentClassName}  ${!(modalDetails === DisplayType.MODAL) || isMobileWidth() ? '' : `flex-v-row spacing-p-t-8 spacing-p-b-8 spacing-p-l-12 spacing-p-r-12  ${styles.rowGapForPanel}`} flex-v-row ${styles.setRowGap}`}
      >
        {illustration ? (
          <div className="flex-center">
            <Thumbnail
              data={{
                imageSrc: getAssetUrl(illustration),
                type: ThumbnailType.IMAGE,
              }}
              className={styles.illustrationDesign}
            />
          </div>
        ) : (
          ''
        )}
        <div className="flexbox flex-v-row">
          <div className={`spacing-m-b-2 flex-row-align-center ${styles.headingSpacing}`}>
            {imgSrc ? (
              <div className={`${styles.thumbnailDesign} bg-neutral-3 radius-8`}>
                <Thumbnail
                  data={{
                    imageSrc: getAssetUrl(imgSrc || ''),
                    type: ThumbnailType.IMAGE,
                  }}
                />
              </div>
            ) : (
              ''
            )}
            <Text dangerouslySetInnerHTML val={heading} size={TextSize.SMALL} bold className="content-body" />
          </div>
          {subheading ? <Text dangerouslySetInnerHTML className="content-body" val={subheading || ''} size={TextSize.XSMALL} /> : ''}
          {listItem}
        </div>

        {showCheckBox ? (
          <div className={`flex-row-align-center ${styles.headingSpacing}`}>
            <Checkbox
              type={checked ? CHECKBOX_TYPE.PRIMARY : CHECKBOX_TYPE.LIGHT}
              isChecked={checked}
              onClick={() => {
                setChecked(!checked);
              }}
              className="_cursor-pointer"
            />

            <Text className="content-body" val={checkBoxText ?? ''} dangerouslySetInnerHTML size={TextSize.XXSMALL} />
          </div>
        ) : null}

        <div className={showButton || showInputBar ? `flex-h-row ${styles.buttonInputSpacing}` : '_hidden'}>
          {showInputBar ? <InputField isDisabled hasIcon={inputBarIcon} input={webpageLink} /> : ''}

          {showButton && buttonComponent ? buttonComponent : null}
          {showButton && buttonComponent === undefined ? (
            <Button
              customClasses={`flex-1 -noun ${styles.noWrapText}`}
              isFullWidth
              disabled={showCheckBox && !checked}
              type={Type.PRIMARY}
              size={Size.SMALL}
              iconPlacement={IconPlacement.LEFT}
              text={buttonText}
              icon={buttonIcon}
              onClick={onClick}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const getLoader = (): ReactElement => {
    return (
      <div className={`${styles.setCustomHeight} flexbox flex-v-row flex-center`}>
        <CircularProgressLoader size={LOADER_SIZE.LARGE} classes="spacing-m-0 spacing-m-b-10" />
        <Text dangerouslySetInnerHTML className="content-sub-text spacing-m-t-10" size={TextSize.SMALL} val={loadingScreenText ?? ''} />
      </div>
    );
  };

  return (
    <Panel
      panelBodyClassName={`.slideIn -from-left shadow-floating-elevation-3 ${!(modalDetails === DisplayType.MODAL) || isMobileWidth() ? `spacing-p-4 flex-v-row ${styles.setRowGap} ${styles.setWidth}` : ''} radius-8 `}
      panelHeader={modalDetails === DisplayType.MODAL ? getHeaderPanel() : getHeaderDropDown()}
      panelContent={isLoading ? getLoader() : getContent()}
    />
  );
}
